import {motion} from 'framer-motion'
import {Divider} from 'primereact/divider'
import {Image} from 'primereact/image'
import {InputText} from 'primereact/inputtext'
import {useMemo} from 'react'
import defaultAvatar from '../../assets/images/avatar.jpg'

import {ReactComponent as DobIcon} from '../../assets/icons/DobIcon.svg'
import {ReactComponent as HospitalIcon} from '../../assets/icons/HospitalIcon.svg'
import {ReactComponent as HospitalNumberIcon} from '../../assets/icons/HospitalNumberIcon.svg'
import {ReactComponent as NHSNumberIcon} from '../../assets/icons/NHSNumberIcon.svg'

import {SystemRole} from '../../Constants'

import logo from '../../assets/icons/TaVIELogo.png'
import {useAppConfig} from '../../context/app/AppContext'
import {useUserHospital} from '../../context/hospital/UserHospitalContext'
import TranslateUtils from '../../i18n/TranslateUtils'
import {capitalize} from '../../utils/CommonUtils'
import {getSuperAdminRole} from '../authentication/AuthUtils'

import moment from 'moment'
import {
    accommodationStatusList,
    diagnosticList,
    identityWithHeldReasonList,
    materialStatusList,
    religionList,
    titleList,
} from '../patient/PatientConstants'
import {convertKeyToLabel} from '../table-utils/TableUtils'
import ObservationPdf from './ObservationPdf'
import {basicPatientDataList, elasticPatientDataList} from './PdfConstants'

const statusMapping = {
    normal: {severity: 'success', value: 'NORMAL', color: 'green'},
    mild: {severity: 'info', value: 'MILD', color: 'blue'},
    moderate: {severity: 'warning', value: 'MODERATE', color: 'orange'},
    severe: {severity: 'danger', value: 'SEVERE', color: 'red'},
}

const PdfContent = ({index, exportData = {}, basicPatient = false}) => {
    // #region Context
    const {userRole} = useAppConfig()
    const {currentHospital} = useUserHospital()
    // #endregion

    //#region Language
    const {translateKeys, translateOptions, translateCommons} = TranslateUtils()
    //#endregion
    //#region Information
    const basicInformationList = useMemo(
        () => [
            {
                label: 'NHS Number',
                value: exportData?.nhsNumber,
                icon: <HospitalIcon />,
            },
            {
                label: 'Gender',
                value: exportData?.gender,
                icon: <HospitalIcon />,
            },
            {
                label: 'Age',
                value: exportData?.age ? `${exportData?.age} years old` : '-',
                icon: <HospitalIcon />,
            },
            {
                label: 'Date of birth',
                value: exportData?.dob,
                icon: <DobIcon />,
            },
            {
                label: 'Hospital Name',
                value: exportData?.hospitalName,
                icon: <HospitalIcon />,
            },
            {
                label: 'Hospital Number',
                value: exportData?.hospitalNumber,
                icon: <HospitalNumberIcon />,
            },
            {
                label: 'Last Known Location',
                value: exportData?.lastLocation,
                icon: <NHSNumberIcon />,
            },

            {
                label: 'Current Location',
                value: exportData?.currentLocation,
                icon: 'pi pi-map-marker',
            },
            {
                label: 'Arrival Date',
                value: exportData?.createdAt
                    ? moment(exportData?.createdAt).format('yyyy-MM-DD HH:mm')
                    : '-',
                icon: <NHSNumberIcon />,
            },

            {
                label: 'Discharge Date',
                value: exportData?.dischargedAt,
                icon: 'pi pi-map-marker',
            },
        ],
        [exportData],
    )
    //#endregion

    //#region Convert Data
    const convertDataToExport = (key, value) => {
        let content = '-'
        if (!value) {
            return '-'
        }

        switch (key) {
            case 'title':
                content =
                    titleList.find(title => title.value === value)?.label || '-'
                break
            case 'gender':
            case 'type':
                content = convertKeyToLabel(value)
                break
            case 'identityWithHeldReason':
                content =
                    translateOptions(identityWithHeldReasonList).find(
                        identity => identity.value === value,
                    ).label || '-'
                break
            case 'religion':
                content =
                    translateOptions(religionList).find(
                        religion => religion.value === value,
                    ).label || '-'
                break
            case 'maritalStatus':
                content =
                    translateOptions(materialStatusList).find(
                        marital => marital.value === value,
                    ).label || '-'
                break
            case 'accommodationStatus':
                content =
                    translateOptions(accommodationStatusList).find(
                        accommodation => accommodation.value === value,
                    ).label || '-'
                break
            case 'diagnostic':
                content =
                    translateOptions(diagnosticList).find(
                        diagnostic => diagnostic.value === value,
                    ).label || '-'
                break
            default:
                content = value
                break
        }

        return content
    }
    //#endregion

    //#region Memo
    const dataList = useMemo(() => {
        const dataListInfo = !!basicPatient
            ? basicPatientDataList
            : elasticPatientDataList

        let defautDataList = [
            {
                key: 'basicInformation',
                label: 'Basic Information',
                icon: 'pi pi-info-circle',
                children: dataListInfo.basicInformation,
            },
            {
                key: 'additionalInformation',
                label: 'Additional Information',
                icon: 'pi pi-user-plus',
                children: dataListInfo.additionalInfomation,
            },
            {
                key: 'contactInformation',
                label: 'Contact Information',
                icon: 'pi pi-book',
                children: dataListInfo.contactInformation,
            },
        ]

        if (!!basicPatient && dataListInfo?.gpInformation) {
            defautDataList.push({
                key: 'gpInformation',
                label: 'General Practitioner Information',
                icon: 'pi pi-users',
                children: dataListInfo.gpInformation,
            })
        }

        return defautDataList
    }, [basicPatient])
    //#endregion

    //#region Render Function
    const renderObsTable = () => {
        let htmlElements = []
        if (
            exportData &&
            !basicPatient &&
            exportData?.healthRecords?.length > 0 &&
            Object.keys(exportData?.healthRecords[0])?.length > 0
        ) {
            const numberTables =
                Math.round(exportData?.healthRecords?.length / 12) + 1
            if (numberTables === 1) {
                htmlElements.push(
                    <div key={1} className="mb-3">
                        <ObservationPdf
                            exportData={exportData?.healthRecords}
                        />
                    </div>,
                )
            } else {
                let holderList = [...exportData?.healthRecords]
                let healthLefts = holderList?.length
                let count = 0
                while (healthLefts > 0) {
                    let tmp
                    if (holderList?.length >= 12) {
                        tmp = holderList.splice(0, 12)
                        healthLefts -= 12
                    } else {
                        tmp = holderList.splice(0, holderList?.length)
                        healthLefts = 0
                    }

                    htmlElements.push(
                        <div key={count}>
                            <ObservationPdf exportData={tmp} />
                        </div>,
                    )
                    count++
                }
            }
        }

        return htmlElements
    }

    const renderDetailContent = () => {
        let contents = []

        if (dataList && dataList?.length > 0) {
            for (let i = 0; i < dataList?.length; i++) {
                contents.push(
                    <div
                        key={dataList[i].key}
                        className="w-full"
                        style={{
                            pageBreakAfter: 'auto',
                            pageBreakInside: 'avoid',
                        }}>
                        <ul className="m-0 p-0">
                            <li className="flex p-2 flex-wrap lightgray">
                                <div className={`pl-2 text-2xl font-bold`}>
                                    <span className="mr-3">
                                        <i
                                            className={`${dataList[i]?.icon} font-bold text-xl`}></i>
                                    </span>
                                    {translateKeys(dataList[i].label)}
                                </div>
                            </li>
                            <li className="col-12 grid py-3 px-0">
                                {dataList &&
                                    dataList[i]?.children &&
                                    dataList[i]?.children?.length > 0 &&
                                    dataList[i]?.children?.map(item => {
                                        return (
                                            <div
                                                key={item?.key}
                                                className={`${
                                                    item.key ===
                                                    'currentLocation'
                                                        ? 'col-12'
                                                        : 'col-4'
                                                } flex align-items-center`}>
                                                <div className="col-12 line-height-3">
                                                    <span
                                                        className={`${'p-float-label'} col-12 p-0`}>
                                                        <InputText
                                                            style={{
                                                                color: 'black',
                                                            }}
                                                            className="custom-detail h-3rem col-12 font-bold pointer-events-none"
                                                            value={
                                                                exportData
                                                                    ? dataList[
                                                                          i
                                                                      ]?.key ===
                                                                      'gpInformation'
                                                                        ? exportData[
                                                                              'generalPractitioner'
                                                                          ] &&
                                                                          convertDataToExport(
                                                                              item.key,
                                                                              exportData[
                                                                                  'generalPractitioner'
                                                                              ][
                                                                                  item
                                                                                      ?.key
                                                                              ],
                                                                          )
                                                                        : convertDataToExport(
                                                                              item.key,
                                                                              exportData[
                                                                                  item
                                                                                      .key
                                                                              ],
                                                                          )
                                                                    : '-'
                                                            }
                                                        />
                                                        <span className="bar"></span>
                                                        <label
                                                            htmlFor={item?.key}>
                                                            {translateKeys(
                                                                item?.label,
                                                            )}
                                                        </label>
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    })}
                            </li>
                        </ul>
                    </div>,
                )
            }
        }

        return contents
    }

    const renderLogo = () => {
        let imgSrc = logo
        let hospitalName = ''

        if (getSuperAdminRole()) {
            return
        }

        if (userRole === SystemRole.ADMIN) {
            imgSrc = exportData?.hospitalLogo ? exportData?.hospitalLogo : logo
            hospitalName = exportData?.hospitalName
                ? exportData?.hospitalName
                : ''
        } else if (userRole === SystemRole.USER_WITH_ROLE) {
            imgSrc = !!currentHospital?.logo ? currentHospital.logo : logo
            hospitalName = !!currentHospital?.name ? currentHospital.name : ''
        }
        return (
            <div className="flex align-items-center w-max p-2">
                <img
                    className="h-4rem m-0 mr-2 bg-white"
                    src={imgSrc}
                    alt="logo"
                />
                <h4 className="m-0 font-bold text-primary">{hospitalName}</h4>
            </div>
        )
    }
    //#endregion

    return (
        <motion.div layout className="col-12 mt-2">
            <Divider type="dashed" />
            {/* <h2 className="font-bold m-0 border-bottom-1">
                {`${translateKeys(
                    `Patient ${!basicPatient ? 'Medication' : ''} Profile`,
                ).toUpperCase()} ${index ? 'NO. ' + index : ''}`}
            </h2> */}
            <div className="flex align-items-center w-full relative p-2">
                {renderLogo()}
            </div>
            <div className="flex flex-column align-items-center w-full">
                {/* <Avatar
                    className="border-circle mb-3 mr-3"
                    image={
                        exportData && exportData.avatar
                            ? exportData.avatar
                            : require('../../assets/images/avatar.jpg')
                    }
                    style={{
                        minHeight: '150px',
                        minWidth: '150px',
                    }}
                    shape="circle"
                /> */}
                {/* <div className="w-full">
                    <div className="grid h-full w-full surface-50 border-round">
                        {profileList.map((item, idx) => (
                            <motion.div
                                whileHover={{
                                    scale: 1.005,
                                }}
                                style={{
                                    minHeight: '150px',
                                }}
                                key={idx}
                                className="md:col-6 col-12">
                                <div className="flex flex-column bg-white h-full w-full p-3">
                                    {' '}
                                    <div className="flex justify-content-between align-items-end">
                                        {' '}
                                        <h6 className="m-0 p-0 font-bold text-700">
                                            {translateKeys(item.label)}{' '}
                                        </h6>
                                        {item.icon}
                                    </div>
                                    <hr />
                                    <div className="flex justify-content-center align-items-center h-full">
                                        <h5 className="text-700 m-0 p-0">
                                            {item.value
                                                ? item.value
                                                : translateCommons('Unknown')}
                                        </h5>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div> */}
                <div className="w-full">
                    <div className="grid h-full">
                        <div className={`col-12`}>
                            <div className="w-full">
                                {/* Patient Information */}
                                <div className="grid relative h-full">
                                    <div className={`col-12`}>
                                        <div
                                            className={`flex justify-content-center gap-5 align-items-center mb-3`}>
                                            {/* Avatar */}
                                            <div>
                                                <Image
                                                    src={
                                                        exportData?.avatar
                                                            ? exportData?.avatar
                                                            : defaultAvatar
                                                    }
                                                    alt="Avatar"
                                                    width={55}
                                                    height={55}
                                                />
                                            </div>

                                            <div className="flex flex-column align-items-center justify-content-between ">
                                                {' '}
                                                <div className="flex">
                                                    <h3 className="font-bold mb-1 text-center mr-3">
                                                        {exportData?.title &&
                                                        exportData.title !==
                                                            'unknown'
                                                            ? `${capitalize(
                                                                  exportData?.title,
                                                              )} `
                                                            : ''}
                                                        {exportData?.fullName
                                                            ? capitalize(
                                                                  exportData?.fullName,
                                                              )
                                                            : '-'}
                                                    </h3>
                                                    {!!statusMapping[
                                                        exportData
                                                            ?.sicknessStatus
                                                    ]?.severity && (
                                                        <span
                                                            className="px-2 flex align-items-center border-round-xl font-bold text-white"
                                                            style={{
                                                                backgroundColor: `${
                                                                    statusMapping[
                                                                        exportData
                                                                            ?.sicknessStatus
                                                                    ].color
                                                                }`,
                                                            }}>
                                                            {
                                                                statusMapping[
                                                                    exportData
                                                                        ?.sicknessStatus
                                                                ].value
                                                            }
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-600 font-semibold text-sm">
                                                    {exportData?.email}
                                                </p>
                                            </div>
                                        </div>

                                        {/* <div className="col-12">
                                            <h5 className="font-semibold text-gray-500  mb-1 uppercase text-lg font-bold text-center">
                                                APPOINTMENTS
                                            </h5>
                                            <div className="col-12 flex justify-content-center">
                                                <div className="w-full text-center">
                                                    <h3 className="font-bold text-darkcyan mb-1">
                                                        5
                                                    </h3>
                                                    <p className="font-semibold text-gray-4">
                                                        Past
                                                    </p>
                                                </div>
                                                <Divider layout="vertical" />
                                                <div className="w-full  text-center">
                                                    <h3 className="font-bold text-darkcyan mb-1">
                                                        4
                                                    </h3>
                                                    <p className="font-semibold text-gray-4">
                                                        Upcoming
                                                    </p>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* <div className="col-12">
                                            <SplitButton
                                                menuClassName="custom-disability"
                                                className="col-12 px-0"
                                                model={items}
                                                severity="info"
                                                icon="pi pi-ellipsis-v"
                                                label="MORE ACTIONS"
                                            />
                                        </div> */}
                                    </div>
                                    <Divider />
                                    <div
                                        className={`grid align-items-strecth col-12`}>
                                        {basicInformationList?.map(
                                            (item, index) => (
                                                <div
                                                    key={`${item?.label}-${index}`}
                                                    className={`lg:col-20 md:col-4 sm:col-6 col-12`}>
                                                    <div className="h-full border-bottom-1 border-100 w-full">
                                                        <h6 className="font-semibold text-gray-500  mb-1 uppercase text-sm font-bold">
                                                            {item?.icon ? (
                                                                typeof item.icon ===
                                                                'string' ? (
                                                                    <i
                                                                        className={`${item.icon} mr-2`}></i>
                                                                ) : (
                                                                    <span className="mr-2">
                                                                        {
                                                                            item.icon
                                                                        }
                                                                    </span>
                                                                )
                                                            ) : (
                                                                ''
                                                            )}
                                                            {
                                                                <span>
                                                                    {
                                                                        item?.label
                                                                    }
                                                                </span>
                                                            }
                                                        </h6>
                                                        <h5
                                                            className="font-bold text-darkcyan"
                                                            style={{
                                                                textTransform:
                                                                    'capitalize',
                                                            }}>
                                                            {item?.value
                                                                ? item.value
                                                                : '-'}
                                                        </h5>
                                                    </div>
                                                </div>
                                            ),
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-column justify-content-start w-full">
                    {exportData &&
                        exportData?.pathways &&
                        exportData?.pathways?.length > 0 &&
                        !!exportData?.pathways[0] && (
                            <div className="w-full">
                                <ul className="m-0 p-0 list-none">
                                    <li className="flex p-2 flex-wrap lightgray">
                                        <div
                                            className={`pl-2 text-2xl font-bold`}>
                                            <span className="mr-3">
                                                <i className="pi pi-directions font-bold text-xl"></i>
                                            </span>
                                            {translateKeys('Pathways')}
                                        </div>
                                    </li>
                                    <li className="p-2">
                                        <div
                                            className={`align-items-center grid justify-content-center col-12`}>
                                            {exportData.pathways.map(
                                                pathway => (
                                                    <img
                                                        key={pathway.name}
                                                        alt={pathway.name}
                                                        height="30"
                                                        className="mx-3"
                                                        src={pathway?.icon}
                                                    />
                                                ),
                                            )}
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        )}
                    {renderDetailContent()}
                    {!basicPatient && (
                        <div
                            className="w-full mb-2"
                            style={{pageBreakBefore: 'always'}}>
                            <ul className="m-0 p-0 col-12">
                                <li className="flex mb-1 border-round justify-content-center align-items-center py-2 px-2 flex-wrap lightgray ">
                                    <div
                                        className={`pl-2 text-2xl font-bold w-full `}>
                                        <span className="mr-3">
                                            <i className="pi pi-heart font-bold text-xl"></i>
                                        </span>
                                        {translateKeys('Health Records')}
                                    </div>
                                </li>

                                <li className="flex align-items-center justify-content-center py-3 flex-wrap col-12">
                                    <div className="w-full">
                                        {renderObsTable()}
                                    </div>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    )
}
export default PdfContent
